/* eslint-disable max-lines -- static notification config is inherently long */
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags";

import { FacilityRoles, FacilityUserPermissions } from "../types";
import { ALERT_TYPE, type WorkplaceNotificationsMap } from "./types";

export const CHARGE_RATE_VISIBLE_EMAIL = "CHARGE_RATE_VISIBLE_EMAIL";
const WORKER_ETA_SMS = "WORKER_ETA_SMS";

export function getDefaultWorkplaceNotifications(): WorkplaceNotificationsMap {
  return {
    EMAIL: {
      INV_EMAIL: {
        mandatoryRoles: [FacilityRoles.INVOICES, FacilityRoles.ADMIN],
        alertTypes: [
          {
            default: {
              enabled: true,
              batch: false,
              userModifiableBatch: false,
              userModifiableState: false,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Invoice Email",
            action: "invoiceEmail",
            role: "INV_EMAIL",
            mandatoryRoles: [FacilityRoles.INVOICES, FacilityRoles.ADMIN],
            mandatoryPermissions: [FacilityUserPermissions.MANAGE_INVOICES],
            validRoles: [FacilityRoles.INVOICES, FacilityRoles.ADMIN],
            validPermissions: [FacilityUserPermissions.MANAGE_INVOICES],
          },
        ],
        type: ALERT_TYPE.EMAIL,
      },
      SFT_EMAIL: {
        alertTypes: [
          {
            default: {
              enabled: true,
              notifyRole: "SFT_EMAIL",
              userModifiableBatch: true,
              userModifiableState: true,
              batch: false,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Shift Assignment",
            action: "assignmentEmail",
            role: "SFT_EMAIL",
            validRoles: [
              FacilityRoles.SHIFT_MANAGEMENT,
              FacilityRoles.ADMIN,
              FacilityRoles.DOCUMENTS,
            ],
            validPermissions: [FacilityUserPermissions.POST_SHIFT_PERMISSION],
          },
          {
            default: {
              enabled: true,
              notifyRole: "SFT_EMAIL",
              userModifiableBatch: true,
              userModifiableState: true,
              batch: false,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Shift Unassign",
            action: "shiftUnassignmentEmail",
            role: "SFT_EMAIL",
            validRoles: [
              FacilityRoles.SHIFT_MANAGEMENT,
              FacilityRoles.ADMIN,
              FacilityRoles.DOCUMENTS,
            ],
            validPermissions: [FacilityUserPermissions.CANCEL_SHIFT_PERMISSION],
          },
          {
            default: {
              enabled: true,
              notifyRole: "SFT_EMAIL",
              batch: false,
              userModifiableBatch: false,
              userModifiableState: true,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Workplace Staffing Summary",
            action: "staffingSummary",
            role: "SFT_EMAIL",
            validRoles: [FacilityRoles.SHIFT_MANAGEMENT, FacilityRoles.ADMIN],
          },
          {
            default: {
              enabled: true,
              notifyRole: "SFT_EMAIL",
              userModifiableBatch: true,
              userModifiableState: true,
              batch: false,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Rate Negotiation",
            action: "rateNegotiationEmail",
            role: "SFT_EMAIL",
            validRoles: [FacilityRoles.SHIFT_MANAGEMENT, FacilityRoles.ADMIN],
          },
          {
            default: {
              enabled: true,
              notifyRole: "SFT_EMAIL",
              batch: false,
              userModifiableBatch: false,
              userModifiableState: true,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Worked Break Payment Request",
            action: "ewtrEmails",
            role: "SFT_EMAIL",
            validRoles: [FacilityRoles.ADMIN, FacilityRoles.INVOICES],
            validPermissions: [FacilityUserPermissions.REVIEW_EXTRA_TIME_PAYMENT_REQUESTS],
          },
          {
            disableBatching: true,
            default: {
              enabled: true,
              notifyRole: "SFT_EMAIL",
              batch: false,
              userModifiableBatch: false,
              userModifiableState: true,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Worker ETA",
            action: "workerEtaEmail",
            role: "SFT_EMAIL",
            validRoles: [
              FacilityRoles.ADMIN,
              FacilityRoles.DOCUMENTS,
              FacilityRoles.INVOICES,
              FacilityRoles.SHIFT_MANAGEMENT,
            ],
          },
          {
            default: {
              enabled: false,
              notifyRole: "SFT_EMAIL",
              batch: false,
              userModifiableBatch: false,
              userModifiableState: true,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Chat messages",
            action: "chatMessages",
            role: "SFT_EMAIL",
            validRoles: [
              FacilityRoles.SHIFT_MANAGEMENT,
              FacilityRoles.ADMIN,
              FacilityRoles.DOCUMENTS,
              FacilityRoles.RECRUITMENT,
            ],
          },
          {
            default: {
              enabled: true,
              notifyRole: "SFT_EMAIL",
              batch: false,
              userModifiableBatch: false,
              userModifiableState: true,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Worker Left Early Replacement",
            action: "wleReplacementEmail",
            role: "SFT_EMAIL",
            validRoles: [FacilityRoles.ADMIN, FacilityRoles.SHIFT_MANAGEMENT],
          },
        ],
        type: ALERT_TYPE.EMAIL,
      },
      CHARGE_RATE_VISIBLE_EMAIL: {
        alertTypes: [
          {
            disableBatching: true,
            default: {
              enabled: true,
              userModifiableBatch: false,
              userModifiableState: true,
              batch: false,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Charge Rates Visibility",
            action: "chargeRateVisibility",
            role: CHARGE_RATE_VISIBLE_EMAIL,
            validRoles: [FacilityRoles.SHIFT_MANAGEMENT, FacilityRoles.ADMIN],
          },
        ],
        type: ALERT_TYPE.EMAIL,
      },
      CDPH530_DAILY_EMAIL: {
        lddFlag: CbhFeatureFlag.CALIFORNIA_TIMESHEET,
        alertTypes: [
          {
            disableBatching: true,
            default: {
              enabled: true,
              userModifiableBatch: false,
              userModifiableState: true,
              batch: false,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Daily CDPH-530 Recap",
            action: "dailyCDPH530Recap",
            role: "CDPH530_DAILY_EMAIL",
            validRoles: [FacilityRoles.ADMIN, FacilityRoles.SHIFT_MANAGEMENT],
          },
        ],
        type: ALERT_TYPE.EMAIL,
      },
    },
    SMS: {
      SFT_SMS: {
        alertTypes: [
          {
            default: {
              enabled: true,
              batch: false,
              notifyRole: "SFT_SMS",
              userModifiableBatch: true,
              userModifiableState: true,
              time: 16,
            },
            type: ALERT_TYPE.SMS,
            title: "Shift Assignment SMS",
            action: "assignmentSMS",
            role: "SFT_SMS",
            validRoles: [
              FacilityRoles.SHIFT_MANAGEMENT,
              FacilityRoles.ADMIN,
              FacilityRoles.DOCUMENTS,
            ],
            validPermissions: [FacilityUserPermissions.POST_SHIFT_PERMISSION],
          },
          {
            default: {
              enabled: true,
              notifyRole: "SFT_SMS",
              batch: false,
              userModifiableBatch: true,
              userModifiableState: true,
              time: 16,
            },
            type: ALERT_TYPE.SMS,
            title: "Shift Unassign SMS",
            action: "shiftUnassignmentSMS",
            role: "SFT_SMS",
            validRoles: [
              FacilityRoles.SHIFT_MANAGEMENT,
              FacilityRoles.ADMIN,
              FacilityRoles.DOCUMENTS,
            ],
            validPermissions: [FacilityUserPermissions.CANCEL_SHIFT_PERMISSION],
          },
          {
            default: {
              enabled: true,
              notifyRole: "SFT_SMS",
              batch: false,
              userModifiableBatch: false,
              userModifiableState: false,
              time: 16,
            },
            type: ALERT_TYPE.SMS,
            title: "Signature Request (By Us)",
            action: "shiftCBHSignatureRequestSMS",
            role: "SFT_SMS",
            validRoles: [FacilityRoles.SHIFT_MANAGEMENT, FacilityRoles.ADMIN],
          },
          {
            default: {
              enabled: true,
              notifyRole: "SFT_SMS",
              batch: false,
              userModifiableBatch: false,
              userModifiableState: true,
              time: 16,
            },
            type: ALERT_TYPE.SMS,
            title: "Chat messages",
            action: "chatMessages",
            role: "SFT_SMS",
            validRoles: [
              FacilityRoles.SHIFT_MANAGEMENT,
              FacilityRoles.ADMIN,
              FacilityRoles.DOCUMENTS,
              FacilityRoles.RECRUITMENT,
            ],
          },
        ],
        type: ALERT_TYPE.SMS,
      },
      WORKER_ETA_SMS: {
        alertTypes: [
          {
            disableBatching: true,
            default: {
              enabled: true,
              notifyRole: WORKER_ETA_SMS,
              batch: false,
              userModifiableBatch: false,
              userModifiableState: true,
              time: 16,
            },
            type: ALERT_TYPE.SMS,
            title: "Worker ETA",
            action: "workerEtaSMS",
            role: WORKER_ETA_SMS,
            validRoles: [
              FacilityRoles.ADMIN,
              FacilityRoles.DOCUMENTS,
              FacilityRoles.INVOICES,
              FacilityRoles.SHIFT_MANAGEMENT,
            ],
          },
        ],
        type: ALERT_TYPE.SMS,
      },
    },
  };
}
/* eslint-enable max-lines */
